// src/pages/user/UserProgressReports.tsx
// Route: /user/progress

import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  useGetProgressReportsQuery,
  useMarkReportReadMutation,
} from "../../app/api/progressReportApi";
import type {
  ProgressReport,
  TechnicalRating,
  EffortRating,
} from "../../app/api/progressReportApi";
import { useGetChildrenQuery } from "../../app/api/parentApi";

import { ArrowRight, Check, ClipboardList, Lightbulb, Pencil, SignalHigh, SignalLow, SignalMedium, TriangleAlert, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import Ico from "../../components/Ico";
/* ─── CONSTANTS ──────────────────────────────────────────────────── */

const RATING_CONFIG: Record<
  TechnicalRating,
  { label: string; color: string; bg: string; border: string }
> = {
  emerging: {
    label: "Emerging",
    color: "#7a4a10",
    bg: "#fff8ee",
    border: "#f5d9a0",
  },
  developing: {
    label: "Developing",
    color: "#4a44b0",
    bg: "#ebeff7",
    border: "#cdd4e2",
  },
  strong: {
    label: "Strong",
    color: "#2a7a56",
    bg: "#edfaf4",
    border: "#b6e8d0",
  },
  excellent: {
    label: "Excellent",
    color: "#1a5a36",
    bg: "#d4f5e6",
    border: "#6cc2a0",
  },
};

const EFFORT_CONFIG: Record<EffortRating, { label: string; icon: LucideIcon }> = {
  low: { label: "Low", icon: SignalLow },
  moderate: { label: "Moderate", icon: SignalMedium },
  high: { label: "High", icon: SignalHigh },
};

const ATTENDANCE_CONFIG = {
  present: {
    label: "Present",
    cls: "bg-[#edfaf4] text-[#2a7a56] border border-[#b6e8d0]",
  },
  late: {
    label: "Late",
    cls: "bg-[#fff8ee] text-[#7a4a10] border border-[#f5d9a0]",
  },
  absent: {
    label: "Absent",
    cls: "bg-[#fff3f3] text-[#9b2a2a] border border-[#f5c0c0]",
  },
  excused: {
    label: "Excused",
    cls: "bg-[#f1f3f8] text-[#8189a0] border border-[#dde2ec]",
  },
};

/* ─── ATOMS ──────────────────────────────────────────────────────── */

function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-xl bg-[#ebeff7] ${className}`} />
  );
}

function Chip({ label, cls }: { label: string; cls: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12.5px] font-semibold ${cls}`}
    >
      {label}
    </span>
  );
}

function RatingBar({ rating }: { rating: TechnicalRating }) {
  const levels: TechnicalRating[] = [
    "emerging",
    "developing",
    "strong",
    "excellent",
  ];
  const idx = levels.indexOf(rating);
  const cfg = RATING_CONFIG[rating];
  return (
    <div className="flex items-center gap-1.5">
      {levels.map((l, i) => (
        <div
          key={l}
          className="h-1.5 flex-1 rounded-full transition-all"
          style={{ background: i <= idx ? cfg.color : "#ebeff7" }}
        />
      ))}
    </div>
  );
}

/* ─── REPORT DETAIL DRAWER ───────────────────────────────────────── */

function ReportDetailDrawer({
  report,
  onClose,
}: {
  report: ProgressReport;
  onClose: () => void;
}) {
  const [markRead] = useMarkReportReadMutation();
  const techCfg =
    RATING_CONFIG[report.technicalRating] ?? RATING_CONFIG.developing;
  const commCfg =
    RATING_CONFIG[report.communicationRating] ?? RATING_CONFIG.developing;
  const effortCfg =
    EFFORT_CONFIG[report.effortRating] ?? EFFORT_CONFIG.moderate;

  // Mark as read when the drawer opens
  if (!report.readByParent) {
    markRead(report.id);
  }

  const attendanceCfg = report.attended
    ? ATTENDANCE_CONFIG.present
    : ATTENDANCE_CONFIG.absent;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#16213e]/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white shadow-[-8px_0_40px_rgba(27,45,94,0.12)] sm:max-w-[480px]"
        role="dialog"
        aria-modal
        aria-label="Progress report"
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[#ebeff7] px-6 py-5">
          <div>
            <h2 className="text-lg font-bold tracking-[-0.03em] text-[#16213e]">
              Week {report.weekNumber} Report
            </h2>
            <p className="mt-0.5 text-xs text-[#8189a0]">
              {report.quarter} · {report.academicYear} ·{" "}
              {report.reportDateLabel ?? report.reportDate}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-xl text-[#8189a0] hover:bg-[#f1f3f8]"
          >
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4l8 8M12 4l-8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Attention flag */}
          {report.requiresParentAttention && (
            <div className="flex items-start gap-3 rounded-[18px] border border-[#f5d9a0] bg-[#fff8ee] px-4 py-3.5">
              <TriangleAlert className="h-5 w-5 flex-shrink-0" aria-hidden />
              <div>
                <p className="text-sm font-bold text-[#7a4a10]">
                  Your attention is needed
                </p>
                <p className="mt-0.5 text-xs text-[#9a6a20]">
                  Your child's tutor has flagged this report. Please read the
                  note below carefully.
                </p>
              </div>
            </div>
          )}

          {/* Attendance + progress hero */}
          <div className="rounded-[20px] border border-[#dde2ec] bg-[#f9fafc] p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                  Attendance
                </p>
                <Chip label={attendanceCfg.label} cls={attendanceCfg.cls} />
              </div>
              <div className="text-right">
                <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                  Progress
                </p>
                <p className="text-2xl font-bold tracking-[-0.04em] text-[#1b2d5e]">
                  {report.progressPercent}%
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-[#ebeff7]">
              <div
                className="h-full rounded-full bg-[#1b2d5e] transition-all duration-700"
                style={{ width: `${report.progressPercent}%` }}
              />
            </div>
          </div>

          {/* Session topic */}
          {report.sessionTopic && (
            <div className="rounded-[16px] border border-[#dde2ec] bg-[#f9fafc] px-4 py-3.5">
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                This week's topic
              </p>
              <p className="mt-1 text-sm font-bold text-[#16213e]">
                {report.sessionTopic}
              </p>
            </div>
          )}

          {/* What was built */}
          {report.whatWasBuilt && (
            <div className="rounded-[16px] border border-[#dde2ec] bg-[#f9fafc] px-4 py-3.5">
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                What was built
              </p>
              <p className="mt-1 text-sm leading-6 text-[#3b4560]">
                {report.whatWasBuilt}
              </p>
            </div>
          )}

          {/* Ratings */}
          <div className="rounded-[20px] border border-[#e5e8ef] overflow-hidden">
            <div className="px-4 py-3 border-b border-[#f7f8fb] bg-[#f9fafc]">
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#8189a0]">
                Ratings
              </p>
            </div>
            <div className="divide-y divide-[#f7f8fb]">
              {/* Technical */}
              <div className="px-4 py-3.5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-[#5a6278]">
                    Technical skill
                  </p>
                  <span
                    className="text-xs font-bold"
                    style={{ color: techCfg.color }}
                  >
                    {techCfg.label}
                  </span>
                </div>
                <RatingBar rating={report.technicalRating} />
              </div>
              {/* Communication */}
              <div className="px-4 py-3.5">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-[#5a6278]">
                    Communication
                  </p>
                  <span
                    className="text-xs font-bold"
                    style={{ color: commCfg.color }}
                  >
                    {commCfg.label}
                  </span>
                </div>
                <RatingBar rating={report.communicationRating} />
              </div>
              {/* Effort */}
              <div className="px-4 py-3.5 flex items-center justify-between">
                <p className="text-xs font-semibold text-[#5a6278]">
                  Effort & engagement
                </p>
                <span className="text-xs font-bold text-[#1b2d5e]">
                  <Ico icon={effortCfg.icon} /> {effortCfg.label}
                </span>
              </div>
            </div>
          </div>

          {/* Skills demonstrated */}
          {report.skillsDemonstrated.length > 0 && (
            <div className="rounded-[16px] border border-[#b6e8d0] bg-[#edfaf4] px-4 py-3.5">
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#2a7a56] mb-2">
                Skills demonstrated <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
              </p>
              <div className="flex flex-wrap gap-1.5">
                {report.skillsDemonstrated.map((s, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center rounded-full bg-[#6cc2a0]/20 border border-[#6cc2a0] px-2.5 py-0.5 text-[12.5px] font-semibold text-[#2a7a56]"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Areas to strengthen */}
          {report.areasToStrengthen.length > 0 && (
            <div className="rounded-[16px] border border-[#cdd4e2] bg-[#ebeff7] px-4 py-3.5">
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-[#4a44b0] mb-2">
                Areas to strengthen{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
              </p>
              <div className="flex flex-wrap gap-1.5">
                {report.areasToStrengthen.map((s, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center rounded-full bg-[#1b2d5e]/15 border border-[#1b2d5e] px-2.5 py-0.5 text-[12.5px] font-semibold text-[#4a44b0]"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Tutor note */}
          {report.tutorNote && (
            <div className="rounded-[20px] border-2 border-[#1b2d5e] bg-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#ebeff7] text-sm">
                  <Pencil className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
                </span>
                <p className="text-[12.5px] font-bold uppercase tracking-wider text-[#1b2d5e]">
                  Note from {report.authorName}
                </p>
              </div>
              <p className="text-sm leading-7 text-[#3b4560] italic">
                "{report.tutorNote}"
              </p>
            </div>
          )}

          {/* Parent action */}
          {report.parentAction && (
            <div className="rounded-[20px] bg-[#16213e] px-5 py-4 text-white">
              <p className="text-[11.5px] font-bold uppercase tracking-wider text-white/50 mb-2">
                Action for you this week
              </p>
              <p className="text-sm leading-6 text-white/90">
                {report.parentAction}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/* ─── REPORT CARD ────────────────────────────────────────────────── */

function ReportCard({
  report,
  onClick,
}: {
  report: ProgressReport;
  onClick: () => void;
}) {
  const techCfg =
    RATING_CONFIG[report.technicalRating] ?? RATING_CONFIG.developing;
  const isUnread = !report.readByParent;
  const childName = report.childName ?? "Unknown child";

  return (
    <button
      onClick={onClick}
      className={[
        "w-full rounded-[22px] border bg-white text-left shadow-[0_2px_8px_rgba(27,45,94,0.04)] transition hover:shadow-[0_6px_20px_rgba(27,45,94,0.10)]",
        isUnread ? "border-[#1b2d5e]" : "border-[#e5e8ef]",
        report.requiresParentAttention ? "ring-2 ring-[#f0a050]/40" : "",
      ].join(" ")}
    >
      {/* Unread dot strip */}
      {isUnread && <div className="h-1 rounded-t-[22px] bg-[#1b2d5e]" />}

      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-bold text-[#16213e]">
                Week {report.weekNumber}
              </p>
              {isUnread && (
                <span className="flex h-1.5 w-1.5 rounded-full bg-[#1b2d5e]" />
              )}
              {report.requiresParentAttention && (
                <TriangleAlert className="h-4 w-4 flex-shrink-0" aria-hidden />
              )}
            </div>
            <p className="mt-0.5 text-xs text-[#8189a0]">
              {report.quarter} · {report.academicYear} ·{" "}
              {report.reportDateLabel ?? report.reportDate}
            </p>
            <p className="mt-0.5 text-xs font-semibold text-[#5a6278]">
              {childName}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            {/* Attendance */}
            <span
              className={[
                "inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-semibold",
                report.attended
                  ? "bg-[#edfaf4] text-[#2a7a56] border border-[#b6e8d0]"
                  : "bg-[#fff3f3] text-[#9b2a2a] border border-[#f5c0c0]",
              ].join(" ")}
            >
              {report.attended ? <><Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> Present</> : <><X className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden /> Absent</>}
            </span>
            {/* Technical rating */}
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[11.5px] font-bold"
              style={{
                background: techCfg.bg,
                color: techCfg.color,
                border: `1px solid ${techCfg.border}`,
              }}
            >
              {techCfg.label}
            </span>
          </div>
        </div>

        {/* Topic */}
        {report.sessionTopic && (
          <p className="mt-2.5 text-xs text-[#5a6278] line-clamp-1">
            <span className="font-semibold text-[#8189a0]">Topic: </span>
            {report.sessionTopic}
          </p>
        )}

        {/* Progress */}
        <div className="mt-3">
          <div className="mb-1 flex justify-between text-[11.5px]">
            <span className="font-semibold text-[#8189a0]">Progress</span>
            <span className="font-bold text-[#1b2d5e]">
              {report.progressPercent}%
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-[#ebeff7]">
            <div
              className="h-full rounded-full bg-[#1b2d5e] transition-all"
              style={{ width: `${report.progressPercent}%` }}
            />
          </div>
        </div>

        {/* Tutor note preview */}
        {report.tutorNote && (
          <p className="mt-3 text-xs italic leading-5 text-[#5f6780] line-clamp-2">
            "{report.tutorNote}"
          </p>
        )}

        {/* Footer */}
        <div className="mt-3.5 flex items-center justify-between">
          <p className="text-[11.5px] text-[#a6adbf]">By {report.authorName}</p>
          <span className="text-xs font-semibold text-[#1b2d5e]">
            Read report{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
          </span>
        </div>
      </div>
    </button>
  );
}

/* ─── SUMMARY STATS ──────────────────────────────────────────────── */

function SummaryStats({ reports }: { reports: ProgressReport[] }) {
  const total = reports.length;
  const unread = reports.filter((r) => !r.readByParent).length;
  const needsAttn = reports.filter((r) => r.requiresParentAttention).length;
  const avgPct =
    total > 0
      ? Math.round(reports.reduce((s, r) => s + r.progressPercent, 0) / total)
      : 0;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {[
        { label: "Reports", value: String(total), color: "#1b2d5e" },
        {
          label: "Unread",
          value: String(unread),
          color: unread > 0 ? "#1b2d5e" : "#a6adbf",
        },
        {
          label: "Needs action",
          value: String(needsAttn),
          color: needsAttn > 0 ? "#f0a050" : "#a6adbf",
        },
        { label: "Avg progress", value: `${avgPct}%`, color: "#6cc2a0" },
      ].map((item) => (
        <div
          key={item.label}
          className="rounded-[18px] border border-[#e5e8ef] bg-white px-4 py-3.5 text-center shadow-[0_2px_8px_rgba(27,45,94,0.04)]"
        >
          <p
            className="text-2xl font-bold tracking-[-0.04em]"
            style={{ color: item.color }}
          >
            {item.value}
          </p>
          <p className="mt-0.5 text-[11.5px] font-semibold uppercase tracking-wider text-[#8189a0]">
            {item.label}
          </p>
        </div>
      ))}
    </div>
  );
}

/* ─── FILTER BAR ─────────────────────────────────────────────────── */

interface Filters {
  childId: string;
  quarter: string;
  unread: boolean;
  needsAttn: boolean;
}

function FilterBar({
  filters,
  onChange,
  children,
  count,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  children: Array<{ id: string; firstName: string; lastName: string }>;
  count: number;
}) {
  const sel =
    "rounded-xl border border-[#dde2ec] bg-white px-3 py-2 text-xs font-semibold text-[#3b4560] outline-none focus:border-[#1b2d5e] cursor-pointer";
  const hasFilters =
    filters.childId || filters.quarter || filters.unread || filters.needsAttn;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <p className="text-sm font-semibold text-[#16213e]">
        {count} report{count !== 1 ? "s" : ""}
      </p>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {children.length > 1 && (
          <select
            value={filters.childId}
            onChange={(e) => onChange({ ...filters, childId: e.target.value })}
            className={sel}
          >
            <option value="">All children</option>
            {children.map((c) => (
              <option key={c.id} value={c.id}>
                {c.firstName} {c.lastName}
              </option>
            ))}
          </select>
        )}
        <select
          value={filters.quarter}
          onChange={(e) => onChange({ ...filters, quarter: e.target.value })}
          className={sel}
        >
          <option value="">All quarters</option>
          <option value="Q1">Q1</option>
          <option value="Q2">Q2</option>
          <option value="Q3">Q3</option>
        </select>
        <button
          onClick={() => onChange({ ...filters, unread: !filters.unread })}
          className={[
            "rounded-xl border px-3 py-2 text-xs font-semibold transition",
            filters.unread
              ? "border-[#1b2d5e] bg-[#ebeff7] text-[#4a44b0]"
              : "border-[#dde2ec] text-[#8189a0] hover:bg-[#f1f3f8]",
          ].join(" ")}
        >
          Unread only
        </button>
        <button
          onClick={() =>
            onChange({ ...filters, needsAttn: !filters.needsAttn })
          }
          className={[
            "rounded-xl border px-3 py-2 text-xs font-semibold transition",
            filters.needsAttn
              ? "border-[#f5d9a0] bg-[#fff8ee] text-[#7a4a10]"
              : "border-[#dde2ec] text-[#8189a0] hover:bg-[#f1f3f8]",
          ].join(" ")}
        >
          <TriangleAlert className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}Needs action
        </button>
        {hasFilters && (
          <button
            onClick={() =>
              onChange({
                childId: "",
                quarter: "",
                unread: false,
                needsAttn: false,
              })
            }
            className="text-[12.5px] font-semibold text-[#1b2d5e] hover:text-[#4a44b0]"
          >
            Clear <X className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── ROOT PAGE ──────────────────────────────────────────────────── */

export default function UserProgressReports() {
  const [selected, setSelected] = useState<ProgressReport | null>(null);
  const [filters, setFilters] = useState<Filters>({
    childId: "",
    quarter: "",
    unread: false,
    needsAttn: false,
  });

  const { data, isLoading, isError, refetch } = useGetProgressReportsQuery(
    {},
    {
      // Poll every 60s so newly published reports appear without manual refresh
      pollingInterval: 60000,
    },
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: childrenData } = useGetChildrenQuery();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allReports: ProgressReport[] = data?.data ?? [];

  const uniqueChildren = useMemo(() => {
    const seen = new Set<string>();
    const result: Array<{ id: string; firstName: string; lastName: string }> =
      [];
    for (const r of allReports) {
      const id = typeof r.child === "string" ? r.child : (r.child as any)?.id;
      if (id && !seen.has(id)) {
        seen.add(id);
        result.push({
          id,
          firstName: r.childName?.split(" ")[0] ?? "",
          lastName: r.childName?.split(" ")[1] ?? "",
        });
      }
    }
    return result;
  }, [allReports]);

  const filtered = useMemo(() => {
    let result = [...allReports];
    if (filters.childId) {
      result = result.filter((r) => {
        const id = typeof r.child === "string" ? r.child : (r.child as any)?.id;
        return id === filters.childId;
      });
    }
    if (filters.quarter)
      result = result.filter((r) => r.quarter === filters.quarter);
    if (filters.unread) result = result.filter((r) => !r.readByParent);
    if (filters.needsAttn)
      result = result.filter((r) => r.requiresParentAttention);
    // Sort: unread + needsAttn first, then newest
    return result.sort((a, b) => {
      const aScore =
        (a.requiresParentAttention ? 2 : 0) + (!a.readByParent ? 1 : 0);
      const bScore =
        (b.requiresParentAttention ? 2 : 0) + (!b.readByParent ? 1 : 0);
      if (bScore !== aScore) return bScore - aScore;
      return b.weekNumber - a.weekNumber;
    });
  }, [allReports, filters]);

  // Use server-provided unreadCount (more accurate — includes reports not yet fetched)
  const unreadCount =
    (data as any)?.unreadCount ??
    allReports.filter((r: ProgressReport) => !r.readByParent).length;

  if (isError)
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
        <p className="text-sm font-semibold text-[#16213e]">
          Couldn't load progress reports
        </p>
        <button
          onClick={refetch}
          className="rounded-2xl bg-[#1b2d5e] px-5 py-2.5 text-xs font-semibold text-white"
        >
          Retry
        </button>
      </div>
    );

  return (
    <>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-[1.6rem] font-bold tracking-[-0.04em] text-[#16213e]">
            Progress Reports
          </h1>
          <p className="mt-1 text-sm text-[#5f6780]">
            Weekly reports from your children's tutors.
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#ebeff7] px-2.5 py-0.5 text-[12.5px] font-bold text-[#4a44b0]">
                {unreadCount} unread
              </span>
            )}
          </p>
        </div>
        <Link
          to="/user/children"
          className="inline-flex flex-shrink-0 items-center gap-2 self-start rounded-2xl border border-[#dde2ec] px-4 py-2.5 text-sm font-semibold text-[#3b4560] hover:bg-[#f1f3f8]"
        >
          View children{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
        </Link>
      </div>

      {/* Stats */}
      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[72px] rounded-[18px]" />
          ))}
        </div>
      ) : allReports.length > 0 ? (
        <SummaryStats reports={allReports} />
      ) : null}

      {/* Filters */}
      {!isLoading && allReports.length > 0 && (
        <div className="mt-5">
          <FilterBar
            filters={filters}
            onChange={setFilters}
            children={uniqueChildren}
            count={filtered.length}
          />
        </div>
      )}

      {/* Report grid */}
      <div className="mt-6">
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-[200px] rounded-[22px]" />
            ))}
          </div>
        ) : allReports.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center rounded-[28px] border border-dashed border-[#cdd4e2] bg-[#f9fafc] px-6 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ebeff7] text-3xl">
              <ClipboardList className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
            </span>
            <h3 className="mt-5 text-lg font-bold tracking-[-0.03em] text-[#16213e]">
              No progress reports yet
            </h3>
            <p className="mt-2 max-w-xs text-sm leading-6 text-[#5f6780]">
              Your children's tutors publish a report every week. Once sessions
              begin, reports will appear here.
            </p>
            <Link
              to="/user/enrolments"
              className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-[#1b2d5e] px-6 py-3 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(27,45,94,0.28)] hover:bg-[#24397a]"
            >
              View enrolments{" "}<ArrowRight className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />
            </Link>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-[24px] border border-[#e5e8ef] bg-[#f9fafc] py-12 text-center">
            <p className="text-sm font-semibold text-[#16213e]">
              No reports match this filter
            </p>
            <button
              onClick={() =>
                setFilters({
                  childId: "",
                  quarter: "",
                  unread: false,
                  needsAttn: false,
                })
              }
              className="mt-3 text-xs font-semibold text-[#1b2d5e]"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((r) => (
              <ReportCard
                key={r.id}
                report={r}
                onClick={() => setSelected(r)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Explainer */}
      {!isLoading && allReports.length > 0 && (
        <div className="mt-8 rounded-[24px] bg-[#16213e] px-6 py-5 text-white">
          <p className="text-sm font-bold"><Lightbulb className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}How to use this report</p>
          <p className="mt-1 text-xs leading-5 text-white/65">
            Every week, your child's tutor writes a personal note covering what
            was built, skills demonstrated, and one action you can take at home.
            Ask your child the three questions:{" "}
            <em>"What did you make? Who does it help? What did you learn?"</em>
          </p>
        </div>
      )}

      {/* Drawer */}
      {selected && (
        <ReportDetailDrawer
          report={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
